import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronRight, ChevronDown } from "lucide-react";
import api from "../lib/api";

// Read-only Relationship View (Lot C1) — renders the aggregated Reference Inspector output.
// One request per detail page. Stable-scoped. Shows only sections with real records.
export function RelationshipView({ stableId, partyType, partyId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!stableId || !partyId) return;
    let alive = true;
    setData(null); setError(false);
    const base = partyType === "organization" ? "organizations" : "persons";
    api.get(`/stables/${stableId}/${base}/${partyId}/relationships`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [stableId, partyType, partyId]);

  if (error) return null;
  if (!data) {
    return (
      <div className="mt-4 flex justify-center py-6" data-testid="relationships-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#9AA39C]" />
      </div>
    );
  }
  const sections = data.sections || [];
  if (sections.length === 0) {
    return (
      <div className="mt-4 bg-white rounded-xl border border-[#E5DEC9] p-5 text-sm text-[#9AA39C]" data-testid="relationships-empty">
        Nessuna relazione operativa in questa scuderia.
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-3" data-testid="relationships-view">
      {sections.map((s) => (
        <div key={s.key} className="bg-white rounded-xl border border-[#E5DEC9] p-5" data-testid={`rel-section-${s.key}`}>
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <p className="font-mono-label text-[10px] uppercase tracking-wide text-[#9AA39C]">{s.title}</p>
            {typeof s.count === "number" && (
              <span className="text-xs text-[#5F6B64]" data-testid={`rel-count-${s.key}`}>{s.count}</span>
            )}
          </div>

          {(s.lines || []).length > 0 && (
            <div className="space-y-1 mb-2">
              {s.lines.map((l, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-[#5F6B64]">{l.label}</span>
                  <span className="font-medium text-[#1B3B2B]" data-testid={`rel-line-${s.key}-${i}`}>{l.value}</span>
                </div>
              ))}
            </div>
          )}

          {s.key === "instructor" && (
            <InstructorDrilldown stableId={stableId} partyId={partyId} />
          )}

          {(s.items || []).length > 0 ? (
            <div className="space-y-1">
              {s.items.map((it, i) => (
                it.link ? (
                  <button key={i} data-testid={`rel-item-${s.key}-${i}`} onClick={() => navigate(it.link)}
                    className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] px-3 py-2 text-left text-sm text-[#1B3B2B] hover:border-[#8C532B]/50 transition-colors">
                    <span className="truncate">{it.label}</span>
                    <ChevronRight className="h-4 w-4 text-[#C9C2B4] shrink-0" />
                  </button>
                ) : (
                  <div key={i} className="text-sm text-[#1B3B2B] px-1">{it.label}</div>
                )
              ))}
            </div>
          ) : (
            typeof s.count === "number" && s.count === 0 && s.empty_note && (
              <p className="text-xs text-[#9AA39C]">{s.empty_note}</p>
            )
          )}
        </div>
      ))}
    </div>
  );
}

const PERIODS = [["7", "7 giorni"], ["30", "30 giorni"], ["90", "90 giorni"]];

// Instructor period drill-down (C4). Prepares operational data for a FUTURE Compensi module.
// NO money/rates. PLANNED (iscritti) and ACTUAL (presenti da Attendance) are shown distinctly.
function InstructorDrilldown({ stableId, partyId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true); setData(null);
    api.get(`/stables/${stableId}/persons/${partyId}/instructor-lessons`, { params: { days } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ lessons: [], lesson_count: 0, total_minutes: 0, total_present: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, days, stableId, partyId]);

  const hours = data ? Math.round((data.total_minutes / 60) * 10) / 10 : 0;

  return (
    <div className="mt-1 border-t border-[#F0EAE1] pt-2" data-testid="instructor-drilldown">
      <button data-testid="instructor-drilldown-toggle" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs text-[#8C532B] hover:text-[#5F4626]">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Dettaglio lezioni per periodo
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div className="inline-flex rounded-lg border border-[#E5DEC9] bg-white p-0.5" data-testid="instructor-period">
            {PERIODS.map(([k, lbl]) => (
              <button key={k} data-testid={`instructor-period-${k}`} onClick={() => setDays(k)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${days === k ? "bg-[#1B3B2B] text-white" : "text-[#5F6B64] hover:text-[#1B3B2B]"}`}>
                {lbl}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-[#9AA39C]" /></div>
          ) : data && (
            <>
              <div className="grid grid-cols-3 gap-2" data-testid="instructor-summary">
                <Kpi label="Lezioni" value={data.lesson_count} testid="kpi-lessons" />
                <Kpi label="Ore" value={hours} testid="kpi-hours" />
                <Kpi label="Presenze" value={data.total_present} testid="kpi-present" />
              </div>
              {data.lessons.length === 0 ? (
                <p className="text-xs text-[#9AA39C]" data-testid="instructor-no-lessons">Nessuna lezione nel periodo.</p>
              ) : (
                <div className="space-y-1">
                  {data.lessons.slice(0, 30).map((l) => (
                    <button key={l.occ_key} data-testid={`instructor-lesson-${l.occ_key}`} onClick={() => navigate("/calendario")}
                      className="w-full flex items-center justify-between gap-2 rounded-lg border border-[#E5DEC9] px-3 py-2 text-left hover:border-[#8C532B]/50 transition-colors">
                      <span className="min-w-0">
                        <span className="block text-sm text-[#1B3B2B] truncate">{l.title}</span>
                        <span className="text-[11px] text-[#9AA39C]">{l.date} · {l.start_time}–{l.end_time} · {l.duration_min} min</span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block text-xs text-[#1B3B2B]" data-testid={`lesson-present-${l.occ_key}`}>{l.present_count} presenti</span>
                        <span className="text-[11px] text-[#9AA39C]">{l.planned_count} iscritti</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-[#FBF6EE] border border-[#E5DEC9] px-2 py-1.5 text-center" data-testid={testid}>
      <div className="text-lg font-semibold text-[#1B3B2B] leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-[#9AA39C]">{label}</div>
    </div>
  );
}

